import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import useDistanceUnitValue from '@common/hooks/useDistanceUnitValue';
import { DistanceValueUnit } from '@common/enums/configuration/units';
import { FleetDashboardFilters } from 'models/fleet-management/FleetDataFilter';
import { FleetDataRobots } from 'models/fleet-management/FleetDataSet';
import { numberFormatter } from 'utils/numbers';
import { formatFilterPeriod } from 'utils/fleet-filters';
import DashboardSection from './DashboardSection';
import StatusCardWidget from './widget/StatusCardWidget';

/** The fleet the filters select, in five figures read from the page's robots. */
const FleetSummary: React.FC<{
  filters: FleetDashboardFilters;
  robotsData: FleetDataRobots[];
}> = ({ filters, robotsData }) => {
  const { t, i18n } = useTranslation();

  const summary = useMemo(
    () => ({
      robotCount: robotsData.length,
      installedCount: robotsData.filter((r) => r.customer).length,
      missionCount: robotsData.reduce((sum, r) => sum + (r.mission_count ?? 0), 0),
      totalArea: robotsData.reduce((sum, r) => sum + (r.total_area ?? 0), 0),
      totalDuration: robotsData.reduce((sum, r) => sum + (r.total_duration ?? 0), 0),
    }),
    [robotsData],
  );

  const { converted: totalCleanedArea, label: totalCleanedAreaLabel } = useDistanceUnitValue(
    summary.totalArea,
    DistanceValueUnit.METERS_SQUARED,
  );

  const hours = summary.totalDuration ?? 0;

  const tiles = [
    {
      key: 'robots',
      value: summary.robotCount,
      label: t('fleet-management-card-robot-count'),
      info: t('fleet-management-info-card-robot-count'),
    },
    {
      key: 'installed',
      value: summary.installedCount,
      label: t('fleet-management-card-installed'),
      info: t('fleet-management-info-card-installed'),
    },
    {
      key: 'missions',
      value: summary.missionCount,
      label: t('fleet-management-card-missions'),
      info: t('fleet-management-info-card-period'),
    },
    {
      key: 'area',
      value: `${numberFormatter(totalCleanedArea, 1)} ${totalCleanedAreaLabel}`,
      label: t('fleet-management-card-cleaned-area'),
      info: t('fleet-management-info-card-period'),
    },
    {
      key: 'time',
      value:
        hours < 24
          ? `${numberFormatter(hours, 0)} ${t('label-hours-short')}`
          : `${numberFormatter(hours / 24, 0)} ${t('label-days-short')}`,
      label: t('fleet-management-card-operating-time'),
      info: t('fleet-management-info-card-period'),
    },
  ];

  return (
    <DashboardSection
      title={t('fleet-management-title-fleet-summary')}
      caption={formatFilterPeriod(filters.start, filters.end, i18n.language)}
      info={t('fleet-management-info-fleet-summary')}
    >
      <Grid container spacing={2}>
        {tiles.map((tile) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={tile.key}>
            {/* A count of robots is neither good nor bad, so it wears no colour. */}
            <StatusCardWidget value={tile.value} label={tile.label} info={tile.info} />
          </Grid>
        ))}
      </Grid>
    </DashboardSection>
  );
};

export default FleetSummary;
